/**
 * AutoMom goal-progress review card.
 *
 * Shown on the Today page when the user completes a task under a goal.
 * AutoMom offers three choices:
 *  ✓ Mark goal done   – all tasks are done, confirm goal is complete
 *  → Get next task    – let AI Breakdown suggest the next step
 *  × Keep going       – dismiss; still have tasks to do
 *  ⋯ Not now         – snooze
 */
import { useEffect, useState } from 'react'
import { CheckCircle2, ChevronRight, Sparkles, Target, X } from 'lucide-react'
import type { GoalReviewPrompt, GoalReviewRespondResult } from '../../services/goalReviewService'
import { BRAND } from '../../config/brand'
import { ORI_KIND_LABEL } from '../../utils/oriAsks'
import { AskEyebrow } from '../today/AskEyebrow'
import { Button } from '../ui/Button'

interface Props {
  prompt: GoalReviewPrompt
  markShown: (id: string) => void
  respond: (id: string, code: 'mark_complete' | 'request_breakdown' | 'keep_going' | 'not_now') => Promise<GoalReviewRespondResult>
  onDone: (result: GoalReviewRespondResult) => void
}

export function GoalProgressReviewCard({ prompt, markShown, respond, onDone }: Props) {
  const [working, setWorking] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    markShown(prompt.id)
  }, [prompt.id, markShown])

  if (done) return null

  const taskLabel = prompt.completed_task_title || 'a task'
  const goalLabel = prompt.goal_title || 'your goal'
  const remaining = prompt.remaining_tasks_count
  const total = prompt.total_tasks_count
  const allDone = prompt.all_tasks_done

  const handle = async (code: 'mark_complete' | 'request_breakdown' | 'keep_going' | 'not_now') => {
    setWorking(true)
    try {
      const result = await respond(prompt.id, code)
      setDone(true)
      onDone(result)
    } catch {
      setWorking(false)
    }
  }

  const progressPct = total > 0 ? Math.round(((total - remaining) / total) * 100) : 100

  return (
    <div className="rounded-2xl border border-brand-subtle-border bg-gradient-to-br from-brand-subtle to-surface p-4 shadow-sm">
      {/* Header */}
      <AskEyebrow icon={<Sparkles />} label={ORI_KIND_LABEL.celebrate} typeWord="Goal" className="mb-2" />
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-primary leading-snug" style={{ fontFamily: 'var(--font-display)' }}>
            {allDone
              ? `All tasks done — is "${goalLabel}" complete?`
              : `Nice work on "${taskLabel}"!`}
          </p>
          <p className="mt-1 text-sm leading-relaxed text-secondary">
            {allDone
              ? `${BRAND.assistant} noticed all tasks under this goal are finished. Want to mark it complete?`
              : remaining === 0
                ? `All tasks under "${goalLabel}" are done, but it still has skills or habits in progress — those take longer, so the goal stays open.`
                : remaining === 1
                  ? `1 task remaining under "${goalLabel}". Want ${BRAND.assistant} to suggest the next step?`
                  : `${remaining} task${remaining !== 1 ? 's' : ''} remaining under "${goalLabel}".`}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      {total > 0 && (
        <div className="mt-2 flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded-full bg-surface-alt overflow-hidden">
            <div
              className="h-full rounded-full bg-brand transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className="text-app-micro text-muted whitespace-nowrap">
            {total - remaining}/{total}
          </span>
        </div>
      )}

      {/* Goal chip */}
      <div className="mt-2 flex items-center gap-1.5">
        <Target className="w-3 h-3 text-brand" />
        <span className="text-app-micro text-brand font-medium truncate">{goalLabel}</span>
      </div>

      {/* Actions */}
      <div className="mt-4 flex flex-wrap gap-2">
        {/* A Needs-you card's answers are outlines (North Star: /today keeps one
            filled button, "Start this"); the green "Mark goal done" and the brand
            "Get next task" were two more. "AI Breakdown" named no one the product
            names; the sentence above already says what the button does. */}
        {allDone && (
          <Button size="xs" variant="outline" disabled={working} onClick={() => void handle('mark_complete')} className="gap-1 hover:bg-ok/10">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Mark goal done
          </Button>
        )}
        <Button size="xs" variant="outline" disabled={working} onClick={() => void handle('request_breakdown')} className="gap-1 hover:bg-brand-subtle">
          <ChevronRight className="w-3.5 h-3.5" />
          {allDone ? 'Plan more steps anyway' : 'Suggest the next step'}
        </Button>
        <Button size="xs" variant="outline" disabled={working} onClick={() => void handle('keep_going')} className="hover:bg-brand-subtle">
          Keep going
        </Button>
        <button
          type="button"
          disabled={working}
          onClick={() => void handle('not_now')}
          className="ml-auto min-h-touch min-w-touch rounded-xl text-muted hover:text-secondary hover:bg-surface-alt disabled:opacity-60 transition-colors inline-flex items-center justify-center"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
